  import React, { useEffect, useState } from "react";
  import Header from "@/components/Header";
  import Search from "@/components/blogs/Search";
  import Sidebar from "@/components/blogs/Sidebar";
  import Grid from "@/components/blogs/Grid";
  import Footer from "@/components/Footer";
  import VideoPlayer from "@/components/VideoPlayer";
  import type { GetStaticProps } from "next";
  import { client } from "@/src/sanity/lib/client";
  import { GET_CATEGORIES, GET_CASE_STUDIES, GET_AUTHORS } from "@/src/sanity/lib/queries";
  import { BtnArrow } from "@/components/SVG";
  import { NextSeo } from "next-seo";
  import { Category, CaseStudy, Author } from "@/utils/types";

  export const getStaticProps: GetStaticProps = async () => {
    const caseStudies: any = await client.fetch(GET_CASE_STUDIES);
    const categories: Category[] = await client.fetch(GET_CATEGORIES);
    const authors: Author[] = await client.fetch(GET_AUTHORS);

    return {
      props: { data: { caseStudies, categories, authors } },
      revalidate: 30,
    };
  };

  export interface CaseStudiesProps {
    data: {
      caseStudies: any;
      categories: Category[];
      authors: Author[];
    };
  }

  const CaseStudiesPage = ({ data }: CaseStudiesProps) => {
    const [filteredCaseStudies, setFilteredCaseStudies] = useState<CaseStudy[]>(data.caseStudies);
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [selectedAuthor, setSelectedAuthor] = useState<string>("");
    const [visibleCount, setVisibleCount] = useState<number>(6);

    useEffect(() => {
      setFilteredCaseStudies(data.caseStudies.slice(0, visibleCount));
    }, [visibleCount, data.caseStudies]);

    

    const handleShowMore = () => {
      setVisibleCount((prevCount) => Math.min(prevCount + 6, data.caseStudies.length));
    };

    const handleShowLess = () => {
      setVisibleCount((prevCount) => Math.max(prevCount - 6, 6));
    };

    return (
      <>
        <NextSeo
  title="AI Case Studies | Planet Hive AI: Proven Real-World Solutions"
  description="Discover Planet Hive AI's real-world case studies showcasing proven AI solutions and innovative strategies for industries like healthcare, finance, and retail. See how we deliver results with AI."
  canonical="https://www.planethive.ai/case-studies" // Correctly placed canonical
  openGraph={{
    url: "https://www.planethive.ai/case-studies",
    title: "AI Case Studies | Planet Hive AI: Proven Real-World Solutions",
    description:
      "Discover Planet Hive AI's real-world case studies showcasing proven AI solutions and innovative strategies for industries like healthcare, finance, and retail. See how we deliver results with AI.",
    images: [
      {
        url: "https://www.planethive.ai/images/planet-hive-logo.png",
        width: 421,
        height: 120,
        alt: "PlanetHive Case Studies Open Graph Image",
              },
            ],
          }}
          twitter={{
            handle: "@Muneb91",
            site: "https://x.com/Muneb91",
            cardType: "summary_large_image",
          }}
        />
        <script
  type="application/ld+json"
  dangerouslySetInnerHTML={{
    __html: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "AI Case Studies | Planet Hive AI",
      "description":
        "Explore detailed case studies from Planet Hive AI, demonstrating successful applications of AI in various industries.",
      "caseStudy": data.caseStudies.map((caseStudy: { 
        title: string; 
        excerpt: string; 
        date: string; 
        mainImage: string; 
        slug: string; 
        challenges: string; 
        solutions: string; 
        results: string; 
        industry: string;
      }) => ({
        "@type": "CaseStudy",
        "headline": caseStudy.title,
        "description": caseStudy.excerpt,
        "datePublished": caseStudy.date || "2024-01-01",
        "image": caseStudy.mainImage || "/images/default-case-study.jpg",
        "url": `https://www.planethive.ai/case-studies/${caseStudy.slug}`,
        "keywords": caseStudy.industry,
        "text": `Challenges: ${caseStudy.challenges}. Solutions: ${caseStudy.solutions}. Results: ${caseStudy.results}.`,
        "author": {
          "@type": "Organization",
          "name": "Planet Hive AI"
        }
      })),
    }),
  }}
/>

        <Header />
        
        {/* Hero Section with Video */}
        <div className="relative w-full h-screen overflow-hidden">
          <VideoPlayer
            src="/video/case-study.mp4" // Correct path to the case study video
            title="Explore Our AI Case Studies"
            description="Discover how PlanetHive has helped businesses implement AI-driven solutions."
            className="hero-video"
          />
          <div className="absolute inset-0 bg-black bg-opacity-40 flex flex-col justify-center items-center">
            <h1 className="text-white font-orbitron text-4xl font-extrabold mb-4">
              Explore Our AI Case Studies
            </h1>
            <p className="text-white text-lg mb-8">
    Discover how PlanetHive has delivered real-world AI success stories through innovative, proven AI solutions across various industries.
  </p>
          </div>
        </div>
        
        <main className="flex flex-col mb-[50px] bg-primary">
          <section className="text-center px-4 py-16">
            <h2 className="text-white font-orbitron text-3xl font-extrabold mb-6">
            Explore Real-World AI Success Stories
            </h2>
            <p className="text-white text-lg mb-8">
              At PlanetHive.ai, we take pride in delivering innovative AI solutions that drive real results for our clients. Our case studies offer a detailed look at how we’ve successfully implemented AI technologies to solve complex challenges across various industries. From optimizing business processes to enhancing customer experiences, these case studies demonstrate the transformative power of AI.
            </p>
            <p className="text-white text-lg mb-8">
              Each case study provides a comprehensive overview of the problems our clients faced, the AI solutions we designed, and the outcomes achieved. Whether you're in healthcare, finance, retail, or another industry, you can find insights on how AI can be applied to your specific challenges. Our case studies also highlight the collaborative approach we take to ensure our clients' unique needs are met, resulting in customized, scalable solutions.
            </p>
            <p className="text-white text-lg mb-8">
    Explore these detailed case studies to gain inspiration and see how AI can drive innovation, streamline operations, and generate measurable results for your business. <a href="/blog" className="text-secondary underline">Read our AI blog</a> for more insights.
  </p>

          </section>

          <Search
            data={data.caseStudies}
            setFilteredData={setFilteredCaseStudies}
            selectedCategories={selectedCategories}
            selectedAuthor={selectedAuthor}
            setSelectedAuthor={setSelectedAuthor}
            authors={data.authors}
          />
          <div className="container mb-20 flex w-full flex-col gap-12 lg:flex-row">
          <Sidebar
  data={filteredCaseStudies}
  categories={data.categories.map(category => ({
    ...category,
    name: `AI Solutions for ${category.name}`, // Keyword-rich names
  }))}
  selectedCategories={selectedCategories}
  route={"case-studies"}
  setSelectedCategories={setSelectedCategories}
/>

            <Grid
    data={filteredCaseStudies.map((caseStudy) => ({
      ...caseStudy,
      detailedDescription: `
        ${caseStudy.excerpt} 
        Challenges faced: ${caseStudy.challenges}. 
        Solutions implemented: ${caseStudy.solutions}. 
        Outcomes achieved: ${caseStudy.results}. 
        Learn how Planet Hive AI delivered measurable results in ${caseStudy.industry}.
      `,
    }))}
    
    route={"case-studies"}
  />
          </div>

          {visibleCount < data.caseStudies.length && (
            <button
              onClick={handleShowMore}
              className="font-inter relative mx-auto mb-16 flex h-16 w-[211px] min-w-44 items-center justify-center rounded-[25px] bg-gradient-to-r from-[#F758D4] to-[#5F16E1] text-center text-xl font-bold text-white after:absolute after:inset-[2px] after:rounded-[25px] after:bg-primary"
              style={{ marginTop: "1.5rem" }}
              aria-label="Show more case studies"
            >
              <span className="relative z-20 flex items-center gap-2 bg-gradient-to-r from-[#F758D4] to-[#5F16E1] bg-clip-text text-transparent">
                Show more <BtnArrow />
              </span>
            </button>
          )}
          {visibleCount > 6 && (
            <button
              onClick={handleShowLess}
              className="font-inter relative mx-auto mb-16 flex h-16 w-[211px] min-w-44 items-center justify-center rounded-[25px] bg-gradient-to-r from-[#F758D4] to-[#5F16E1] text-center text-xl font-bold text-white after:absolute after:inset-[2px] after:rounded-[25px] after:bg-primary"
              style={{ marginTop: "1.5rem" }}
              aria-label="Show less case studies"
            >
              <span className="relative z-20 flex items-center gap-2 bg-gradient-to-r from-[#F758D4] to-[#5F16E1] bg-clip-text text-transparent">
                Show less <BtnArrow />
              </span>
            </button>
            
          )}
        </main>
        <Footer />
      </>
    );
  };

  export default CaseStudiesPage;
